import { z } from 'zod';

const offerSchema = z.object({
  offerName: z.string().min(1, 'Offer name is required'),
  offerLink: z.string().url('Please enter a valid URL'),
  entryDomainId: z.string().min(1, 'Entry domain is required'),
  redirectDomainId: z.string().min(1, 'Redirect domain is required'),
  offerUniqueId: z.string(),
});

const x1ParameterSchema = z.object({
  tag: z
    .string()
    .regex(/^[a-zA-Z0-9]+$/, 'Tag must contain only letters and numbers')
    .min(1, 'Tag is required'),
  autogenerateRule: z.number().min(0, 'Autogenerate rule must be a number').max(3, 'Autogenerate rule must be a number'),
  length: z.number().min(1, 'Length must be at least 1'),
  prefix: z.string(),
  replacementMethod: z.any(),
});

const trafficSourceSchema = z.object({
  network: z.enum(['adnet', 'xml', 'default'], { message: 'Network is required' }),
  xmlSample: z.string().min(1, 'Required'),
  xmlRedirectField: z.string().min(1, 'Required'),
  autoCreateTrafficSource: z.boolean(),
  selectedTrafficSourceId: z.string().min(1, 'Required'),

  trafficSourceName: z.string().min(1, 'Required'),
  x1Parameters: z.array(x1ParameterSchema).min(1, 'At least 1 parameter is required'),
  trafficSourceIronDomeFile: z.any(),
});

const campaignSchema = z.object({
  autoCreateCampaign: z.boolean(),
  selectedCampaignId: z.string().min(1, 'Required'),

  campaignName: z.string().min(1, 'Required'),
  campaignEntryDomainId: z.string().min(1, 'Required'),
  offerGroupId: z.string().min(1, 'Required'),
  campaignIronDomeFile: z.any(),
  merchantId: z.string().min(1, 'Required'),
  merchantName: z.string().min(1, 'Required'),
  merchantUrl: z.string().min(1, 'Required'),
  affiliateUrl: z.string().url('Please enter a valid URL').min(1, 'Required'),
  clickCap: z.number({ message: 'Click cap must be a number' }),
  commission: z.number({ message: 'Commission must be a number' }),
  geo: z.string(),
  device: z.string(),
});

const wizardSchema = z.intersection(offerSchema, z.intersection(trafficSourceSchema, campaignSchema));

export { wizardSchema, offerSchema, trafficSourceSchema, campaignSchema };

// Type inference
export type OfferFormData = z.infer<typeof offerSchema>;
export type TrafficSourceFormData = z.infer<typeof trafficSourceSchema>;
export type CampaignFormData = z.infer<typeof campaignSchema>;
export type WizardFormData = z.infer<typeof wizardSchema> & { offerUniqueId: string };

const createDefaultX1Parameters = () => {
  const defaultTags = [
    { tag: 'x1', autogenerateRule: 0 },
    { tag: 'x2', autogenerateRule: 3 },
    { tag: 'x3', autogenerateRule: 3 },
    { tag: 'x4', autogenerateRule: 3 },
    { tag: 'x5', autogenerateRule: 0 },
    { tag: 'x6', autogenerateRule: 3 },
    { tag: 'x7', autogenerateRule: 3 },
    { tag: 'x8', autogenerateRule: 3 },
    { tag: 'url', autogenerateRule: 0 },
    { tag: 'clkid', autogenerateRule: 3 },
    { tag: 'ip', autogenerateRule: 0, defaultValue: 'VISITOR_IP' },
    { tag: 'ua', autogenerateRule: 0, defaultValue: 'VISITOR_UA' },
  ];

  return defaultTags.map((item) => ({
    ...item,
    length: 7,
    prefix: '',
    replacementMethod: 'dynamicParameter',
  }));
};

// Default values
export const defaultWizardValues: WizardFormData = {
  offerName: '',
  offerLink: '',
  entryDomainId: '',
  redirectDomainId: '',

  network: 'default',
  xmlSample: '',
  xmlRedirectField: '',
  autoCreateTrafficSource: true,
  selectedTrafficSourceId: '',

  trafficSourceName: '',
  x1Parameters: createDefaultX1Parameters(),
  trafficSourceIronDomeFile: null,

  autoCreateCampaign: true,
  selectedCampaignId: '',

  campaignName: '',
  campaignEntryDomainId: '',
  offerGroupId: '',
  campaignIronDomeFile: null,
  merchantId: '',
  merchantName: '',
  merchantUrl: '',
  affiliateUrl: '',
  clickCap: 0,
  commission: 0,
  geo: '',
  device: '',

  offerUniqueId: '',
};
